// Active-tenant switching for operators who belong to multiple companies.
// betonijerry has no global "active company" picker of its own — it reuses the
// betoni.online backend endpoints. The active company is the JWT's ownerAsiakasId
// claim, so switching means re-minting the token (server-side, membership
// re-verified against the DB — IDOR-safe).

import { API_URL } from "src/utils/apiUrl";
import { jsonOrThrow, authHeaders } from "src/utils/apiFetch";

const jsonHeaders = (token) => ({ "Content-Type": "application/json", ...authHeaders(token) });

// personSettingTypeId for OLETUS_ADMIN_YRITYS_ID (the user's default company).
// Mirrors @ibetoni/constants PERSON_SETTING_TYPE_IDS.OLETUS_ADMIN_YRITYS_ID = 15;
// betonijerry is outside the npm workspace so the constant can't be imported.
const OLETUS_ADMIN_YRITYS_ID = 15;

/**
 * POST /api/company-selection/switch — re-mint the JWT with a new active company.
 * The backend re-derives roles fresh and verifies membership against the DB, so a
 * caller can only switch to a company they actually belong to.
 * @returns the response body `{ token, ownerAsiakasId, ownerAsiakasName, ... }`
 *          (sendSuccess sends it unwrapped). Throws (with `.status`) on non-2xx.
 */
export async function switchActiveCompany({ token, newAsiakasId }) {
    const res = await fetch(`${API_URL}/api/company-selection/switch`, {
        method: "POST",
        headers: jsonHeaders(token),
        body: JSON.stringify({ newAsiakasId }),
    });
    return jsonOrThrow(res);
}

/**
 * POST /api/person/settings/set — persist the active company as the user's default
 * (typeId 15) so the next login mints the JWT on this company. The route lets a
 * person write their own settings, so no admin role is needed.
 *
 * Call this fire-and-forget: a failure only loses the cross-login default; the
 * in-session switch (token swap) has already taken effect.
 */
export async function persistDefaultCompany({ token, personId, asiakasId }) {
    const res = await fetch(`${API_URL}/api/person/settings/set/`, {
        method: "POST",
        headers: jsonHeaders(token),
        body: JSON.stringify({
            personSettingTypeId: OLETUS_ADMIN_YRITYS_ID,
            personId,
            boolVar: true,
            intVar: asiakasId,
            stringVar: null,
            timeVar: null,
        }),
    });
    return jsonOrThrow(res);
}
